import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { storeConfig } from '../../../../store.config'
import { APIBASE } from '../../../constants/api.constants'
import { APIROUTES, ROUTES, fillPath } from '../../../constants/routes.constants'
import { Divider, StorefrontBackdrop } from '../../components/storefront/chrome'
import { Spinner } from '../../components/atoms/Spinner'
import { DURATION, EASE, Reveal } from '../../motion'
import { useAuth } from '../../../context/AuthContext'
import { useCartContext } from '../../../context/CartContext'
import { useProfileContext } from '../../../context/ProfileContext'
import type { CartLineDto } from '../../../models/cart.model'

/**
 * Cart as a right-anchored slide-OVER sheet (mirrors StoryOverlay) — opens on
 * the cart nav button, the underlying page stays visible-dimmed on the left.
 * Slides in right-to-left (~0.7s), backdrop dim + X/Esc/click-backdrop closes,
 * body scroll locked while open.
 *
 * SoC: presentation only. State + mutations come from CartContext. The list
 * UI (lines, qty stepper, remove, summary) lives here; the cart wire shape +
 * fetch + cache live in useCart/CartContext.
 */
export function CartOverlay({ onClose }: { readonly onClose: () => void }): JSX.Element {
  const reduce = useReducedMotion() === true
  const { kind, cart, isLoading, error, refresh, setItemQuantity, removeItem, confirmOrder, confirmBuildRequest, requestQuoteEmail } = useCartContext()
  const isBuild = kind === 'build'
  const profile = useProfileContext()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [confirmedOrder, setConfirmedOrder] = useState<{
    readonly id: string
    readonly subtotal_paisa: number
    readonly item_count: number
  } | null>(null)
  /** Set when a /build Request lands. Mutually exclusive with
   *  confirmedOrder — the drawer only renders one confirmation panel
   *  at a time (depending on the surface). */
  const [confirmedBuildRequest, setConfirmedBuildRequest] = useState<{
    readonly id: string
    readonly item_count: number
  } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Self-serve PDF-quote email flow (Kiran 2026-06-28). `quoteState`
  // drives the button's transient "Sending…" / "Sent" / error labels;
  // 'sent' auto-resets after a short delay so the customer can re-send
  // a different cart later without reloading.
  const [quoteState, setQuoteState] = useState<
    { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'sent'; to: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' })
  // null = unknown (checking); the drawer briefly shows a small spinner
  // for the address chip while this resolves. Re-checked every time the
  // overlay opens so a freshly-added address (post-return) is picked up.
  const [hasAddress, setHasAddress] = useState<boolean | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      setHasAddress(null)
      return
    }
    let cancelled = false
    setHasAddress(null)
    ;(async () => {
      try {
        // The D1-backed user address lives on /account/profile (single
        // address per user). The legacy /account/addresses surface
        // (plural CRUD) reads a CONFIG_KV mock-Shopify store and is
        // not where Profile-screen edits land — using it caused the
        // bug where the cart claimed the user had no address even
        // when one was saved (Kiran 2026-06-04).
        const res = await fetch(
          `${APIBASE}${APIROUTES.ACCOUNTPROFILE}`,
          { credentials: 'include' }
        )
        if (cancelled) return
        if (!res.ok) {
          setHasAddress(false)
          return
        }
        const body = (await res.json()) as {
          readonly success?: boolean
          readonly data?: { readonly address?: unknown }
        }
        if (!cancelled) setHasAddress(body.data?.address != null)
      } catch {
        if (!cancelled) setHasAddress(false)
      }
    })()
    return () => { cancelled = true }
  }, [isAuthenticated])

  // Body scroll lock + Esc-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  // Refresh on open in case items changed since the last sync.
  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasItems = cart.items.length > 0

  return (
    <motion.div
      className="fixed inset-0 z-50 flex justify-end bg-charcoal/40"
      role="dialog"
      aria-modal="true"
      aria-label={isBuild ? 'Your selection' : 'Your cart'}
      onClick={onClose}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      transition={{ duration: 0.4 }}
    >
      <motion.div
        className="relative h-full w-full max-w-2xl overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        variants={{ hidden: { x: reduce ? 0 : '100%' }, visible: { x: 0 } }}
        transition={{ duration: reduce ? 0.2 : DURATION.standard, ease: EASE }}
      >
        <StorefrontBackdrop />

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-bg-primary/70 text-charcoal backdrop-blur-md transition hover:text-forest sm:right-6 sm:top-6"
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="relative z-[1] flex min-h-full flex-col px-6 py-12 sm:px-10">
          <header className="flex items-center justify-between gap-3">
            <h2 className="font-heading text-h2 text-charcoal">{isBuild ? 'Your selection' : 'Your cart'}</h2>
            {hasItems ? (
              <p className="font-body text-body-sm text-text-secondary">
                {cart.item_count} item{cart.item_count === 1 ? '' : 's'}
              </p>
            ) : null}
          </header>

          <div className="my-5">
            <Divider />
          </div>

          {error !== null ? (
            <div className="mb-4 rounded-md border border-terra/30 bg-terra/10 px-4 py-3 font-body text-body-sm text-terra">
              {error}{' '}
              <button type="button" onClick={() => void refresh()} className="underline hover:text-charcoal">
                Try again
              </button>
            </div>
          ) : null}

          {/* Inline confirmation banners removed 2026-06-08 — the full-
              screen cinematic confirmation (ConfirmationOverlay below)
              is the closing moment now. The state is still tracked so
              the drawer can suppress the EmptyState until the overlay
              dismisses. */}

          {isLoading && !hasItems ? (
            <div className="flex flex-1 items-center justify-center py-16">
              <Spinner />
            </div>
          ) : !hasItems ? (
            <EmptyState
              isBuild={isBuild}
              onContinue={() => { onClose(); navigate(isBuild ? ROUTES.BUILD : ROUTES.SHOP) }}
            />
          ) : (
            <>
              <motion.ul
                className="space-y-3"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: { transition: { staggerChildren: reduce ? 0 : 0.06 } }
                }}
              >
                <AnimatePresence initial={false}>
                  {cart.items.map((line) => (
                    <motion.li
                      key={line.variant_id}
                      layout
                      variants={{
                        hidden: { opacity: 0, y: reduce ? 0 : 12 },
                        visible: { opacity: 1, y: 0 }
                      }}
                      exit={{ opacity: 0, y: reduce ? 0 : -8 }}
                      transition={{ duration: reduce ? 0.2 : DURATION.standard, ease: EASE }}
                    >
                      <CartLine
                        line={line}
                        onUpdate={(q) => { void setItemQuantity(line.variant_id, q) }}
                        onRemove={() => { void removeItem(line.variant_id) }}
                      />
                    </motion.li>
                  ))}
                </AnimatePresence>
              </motion.ul>

              <Reveal as="section" amount={0.05} className="mt-8 rounded-2xl border border-border/40 bg-bg-primary/72 p-6 shadow-md backdrop-blur-md">
                <h3 className="font-heading text-h3 text-charcoal">Summary</h3>
                <dl className="mt-4 space-y-2 font-body text-body">
                  <div className="flex justify-between">
                    <dt className="text-text-secondary">Items</dt>
                    <dd className="font-mono tabular-nums">{cart.item_count}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-text-secondary">Subtotal</dt>
                    <dd className="font-mono tabular-nums">{formatRupees(cart.subtotal_paisa)}</dd>
                  </div>
                </dl>
                {!isAuthenticated ? (
                  // Guest: NO Confirm button. CTA + Sign in. Cart merges
                  // into the customer cart on login (sessionResponse) so the
                  // items survive the round trip.
                  <div className="mt-6 rounded-2xl border border-border/40 bg-bg-primary/72 px-4 py-4 text-center shadow-sm backdrop-blur-md">
                    <p className="font-body text-body-sm text-charcoal">
                      Sign in to place an order.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        // Preserve current storefront URL so login lands
                        // the customer back where they were (e.g. /build →
                        // /login → /build). Kiran 2026-06-06.
                        const from = location.pathname
                        onClose()
                        navigate(ROUTES.LOGIN, { state: { from } })
                      }}
                      className="mt-3 inline-block rounded-full bg-forest px-6 py-2 font-body text-body-sm text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg"
                    >
                      Sign in
                    </button>
                  </div>
                ) : isBuild ? (
                  // /build: Request button (Kiran 2026-06-06). On click
                  // we POST /build/requests/confirm — the server snapshots
                  // the cart into build_requests + build_request_items
                  // (sister of orders / order_items) and tears down the
                  // build lines. The shop cart and the carts row survive.
                  <button
                    type="button"
                    onClick={async () => {
                      if (submitting) return
                      setSubmitting(true)
                      const r = await confirmBuildRequest()
                      setSubmitting(false)
                      if (r !== null) {
                        setConfirmedBuildRequest({
                          id: r.build_request_id,
                          item_count: r.item_count
                        })
                      }
                    }}
                    disabled={submitting}
                    className="mt-6 w-full rounded-full bg-forest px-6 py-3 font-body text-body text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg disabled:cursor-not-allowed disabled:bg-forest/40 disabled:hover:shadow-md"
                  >
                    {submitting ? 'Submitting request…' : 'Request'}
                  </button>
                ) : hasAddress === null ? (
                  <div className="mt-6 flex items-center justify-center py-3">
                    <Spinner />
                  </div>
                ) : hasAddress === false ? (
                  // Signed in but no delivery address (D1-backed user_addresses).
                  // CTA opens the Profile overlay with returnToCart so a
                  // successful save bounces the user straight back to the cart
                  // (ProfileProvider handles the round trip in-memory; no URL
                  // round trip).
                  <div className="mt-6 rounded-2xl border border-border/40 bg-bg-primary/72 px-4 py-4 text-center shadow-sm backdrop-blur-md">
                    <p className="font-body text-body-sm text-charcoal">
                      Add a delivery address to place your order.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        onClose()
                        profile.open({ returnToCart: true })
                      }}
                      className="mt-3 inline-block rounded-full bg-forest px-6 py-2 font-body text-body-sm text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg"
                    >
                      Add address
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      if (submitting) return
                      setSubmitting(true)
                      const r = await confirmOrder()
                      setSubmitting(false)
                      if (r !== null) {
                        setConfirmedOrder({
                          id: r.order_id,
                          subtotal_paisa: r.subtotal_paisa,
                          item_count: r.item_count
                        })
                      }
                    }}
                    disabled={submitting}
                    className="mt-6 w-full rounded-full bg-forest px-6 py-3 font-body text-body text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg disabled:cursor-not-allowed disabled:bg-forest/40 disabled:hover:shadow-md"
                  >
                    {submitting ? 'Placing order…' : 'Confirm order'}
                  </button>
                )}
                {!isBuild && isAuthenticated && cart.items.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={async () => {
                        if (quoteState.kind === 'sending') return
                        setQuoteState({ kind: 'sending' })
                        const r = await requestQuoteEmail()
                        if (r.ok) {
                          setQuoteState({ kind: 'sent', to: r.sent_to })
                          window.setTimeout(() => setQuoteState({ kind: 'idle' }), 4000)
                        } else {
                          setQuoteState({ kind: 'error', message: r.message })
                        }
                      }}
                      disabled={quoteState.kind === 'sending'}
                      className="mt-3 w-full rounded-full border border-forest/40 bg-bg-primary px-6 py-2 font-body text-body-sm text-charcoal transition hover:border-forest hover:bg-forest/10 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {quoteState.kind === 'sending' ? 'Emailing quote…'
                        : quoteState.kind === 'sent' ? `Quote sent to ${quoteState.to}`
                        : 'Email me a quote (PDF)'}
                    </button>
                    {quoteState.kind === 'error' ? (
                      <p className="mt-2 text-center font-body text-body-xs text-terra">{quoteState.message}</p>
                    ) : null}
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={() => { onClose(); navigate(isBuild ? ROUTES.BUILD : ROUTES.SHOP) }}
                  className="mt-4 block w-full text-center font-body text-body-sm text-charcoal underline-offset-2 hover:underline"
                >
                  {isBuild ? '← Continue building' : '← Continue shopping'}
                </button>
              </Reveal>
            </>
          )}
        </div>
      </motion.div>

      {/* Cinematic closing moment — the order/request landing is the most
          emotionally weighted step in the funnel, so it gets a full-bleed
          fade-to-black, brand mark, and receipt summary on the EASE curve.
          The drawer itself stays mounted behind so dismissing the overlay
          reveals the now-empty cart underneath. Kiran 2026-06-08. */}
      <AnimatePresence>
        {confirmedOrder !== null ? (
          <ConfirmationOverlay
            heading="Order placed"
            subhead={`${confirmedOrder.item_count} item${confirmedOrder.item_count === 1 ? '' : 's'} · ${formatRupees(confirmedOrder.subtotal_paisa)}`}
            ctaLabel="See your orders"
            onCta={() => {
              setConfirmedOrder(null)
              onClose()
              navigate(ROUTES.ACCOUNTORDERS)
            }}
            onDismiss={() => setConfirmedOrder(null)}
          />
        ) : null}
        {confirmedBuildRequest !== null ? (
          <ConfirmationOverlay
            heading="Request submitted"
            subhead={`${confirmedBuildRequest.item_count} item${confirmedBuildRequest.item_count === 1 ? '' : 's'} · we'll be in touch about your store.`}
            ctaLabel="Back to building"
            onCta={() => {
              setConfirmedBuildRequest(null)
              onClose()
              navigate(ROUTES.BUILD)
            }}
            onDismiss={() => setConfirmedBuildRequest(null)}
          />
        ) : null}
      </AnimatePresence>
    </motion.div>
  )
}

/**
 * Full-screen cinematic moment shown after an order lands or a build
 * request is submitted. The drawer remains mounted under it so closing
 * this overlay returns the customer to the now-empty cart, which keeps
 * the spatial model intact. EASE curve at hero duration.
 */
function ConfirmationOverlay({
  heading,
  subhead,
  ctaLabel,
  onCta,
  onDismiss
}: {
  readonly heading: string
  readonly subhead: string
  readonly ctaLabel: string
  readonly onCta: () => void
  readonly onDismiss: () => void
}): JSX.Element {
  const reduce = useReducedMotion() === true
  const { brand } = storeConfig
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onDismiss()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onDismiss])
  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal text-text-light"
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduce ? 0.2 : DURATION.standard, ease: EASE }}
      onClick={onDismiss}
    >
      <motion.div
        className="relative mx-auto max-w-lg px-8 text-center"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: reduce ? 0 : 32 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: reduce ? 0 : 16 }}
        transition={{ duration: reduce ? 0.2 : DURATION.hero, ease: EASE, delay: reduce ? 0 : 0.15 }}
      >
        {brand.nameScript !== null ? (
          <p className="font-kannada text-h1 leading-none" style={{ color: 'var(--color-primary)' }}>
            {brand.nameScript}
          </p>
        ) : null}
        <h2 className="mt-6 font-heading text-h1 leading-tight">{heading}</h2>
        <p className="mt-3 font-body text-body opacity-80">{subhead}</p>
        <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={onCta}
            className="rounded-full bg-forest px-7 py-3 font-body text-body text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg"
          >
            {ctaLabel}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-full border border-text-light/30 px-7 py-3 font-body text-body text-text-light transition hover:border-text-light"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

/* ───────────────── pieces ───────────────── */

function EmptyState({
  isBuild,
  onContinue
}: {
  readonly isBuild: boolean
  readonly onContinue: () => void
}): JSX.Element {
  return (
    <div className="mx-auto my-auto max-w-md rounded-[2rem] border border-border/40 bg-bg-primary/72 px-8 py-12 text-center shadow-md backdrop-blur-md">
      <h3 className="font-heading text-h3 text-charcoal">
        {isBuild ? 'Your selection is empty' : 'Your cart is empty'}
      </h3>
      <p className="mt-3 font-body text-body text-text-secondary">
        {isBuild
          ? 'Pick products from the build catalogue to feature in your store.'
          : 'Browse the rocker and add it here when you’re ready.'}
      </p>
      <button
        type="button"
        onClick={onContinue}
        className="mt-6 inline-block rounded-full bg-forest px-6 py-3 font-body text-body text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg"
      >
        {isBuild ? 'Continue building' : 'Continue shopping'}
      </button>
    </div>
  )
}

function CartLine({
  line,
  onUpdate,
  onRemove
}: {
  readonly line: CartLineDto
  readonly onUpdate: (quantity: number) => void
  readonly onRemove: () => void
}): JSX.Element {
  // Effective cap = whichever ceiling runs out first: stock or per-product
  // max_order_quantity (null = no per-order limit).
  const cap = line.max_order_quantity === null
    ? line.on_hand
    : Math.min(line.on_hand, line.max_order_quantity)
  const canDec = line.quantity > 1
  const canInc = line.quantity < cap
  const [draft, setDraft] = useState<string>(String(line.quantity))
  useEffect(() => { setDraft(String(line.quantity)) }, [line.quantity])

  const commit = (raw: string): void => {
    const n = Number(raw)
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
      setDraft(String(line.quantity))
      return
    }
    const clamped = Math.min(cap, Math.max(1, n))
    if (clamped !== line.quantity) onUpdate(clamped)
    setDraft(String(clamped))
  }

  return (
    <div className="flex gap-4 rounded-2xl border border-border/40 bg-bg-primary/72 p-4 shadow-sm backdrop-blur-md">
      <Thumbnail mediaId={line.thumbnail_media_id} alt={line.product_name} />
      <div className="flex flex-1 flex-col">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-heading text-body-lg text-charcoal leading-tight">{line.product_name}</p>
            {line.axes_label !== null ? (
              <p className="mt-1 font-body text-body-sm text-text-secondary">{line.axes_label}</p>
            ) : null}
          </div>
          <p className="font-mono text-body text-charcoal tabular-nums">
            {line.line_total_paisa !== null ? formatRupees(line.line_total_paisa) : '—'}
          </p>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center rounded-full border border-border bg-bg-primary/60">
            <button
              type="button"
              onClick={() => onUpdate(line.quantity - 1)}
              disabled={!canDec}
              aria-label="Decrease quantity"
              className="flex h-8 w-8 items-center justify-center rounded-full text-h3 text-charcoal transition hover:bg-forest/10 disabled:cursor-not-allowed disabled:opacity-30"
            >
              −
            </button>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={cap}
              step={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={(e) => commit(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
              aria-label="Quantity"
              className="h-8 w-12 bg-transparent text-center font-mono text-body-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-forest/40 rounded"
            />
            <button
              type="button"
              onClick={() => onUpdate(line.quantity + 1)}
              disabled={!canInc}
              aria-label="Increase quantity"
              title={!canInc ? `Maximum ${cap}` : undefined}
              className="flex h-8 w-8 items-center justify-center rounded-full text-h3 text-charcoal transition hover:bg-forest/10 disabled:cursor-not-allowed disabled:opacity-30"
            >
              +
            </button>
          </div>

          <button
            type="button"
            onClick={onRemove}
            className="font-body text-body-sm text-text-secondary underline-offset-2 hover:text-terra hover:underline"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}

function Thumbnail({ mediaId, alt }: { readonly mediaId: string | null; readonly alt: string }): JSX.Element {
  if (mediaId === null) {
    return (
      <div className="h-20 w-20 flex-shrink-0 rounded-xl border border-border/30 bg-parchment/40" aria-hidden />
    )
  }
  const src = `${APIBASE}${fillPath(APIROUTES.SHOPMEDIA, { id: mediaId })}`
  return (
    <img
      src={src}
      alt={alt}
      className="h-20 w-20 flex-shrink-0 rounded-xl border border-border/30 object-cover"
    />
  )
}

function formatRupees(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
